import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Background,
  BackgroundVariant,
  Controls,
  ReactFlow,
  applyNodeChanges,
  type BuiltInEdge,
  type Edge,
  type Node,
  type NodeChange,
  type NodeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type { JourneyGraph } from '@ederest/contracts';
import {
  FRICTION_LEVEL_LABELS,
  FRICTION_LEVEL_RANGES,
  type FrictionLevel,
} from '../../../shared/ui/FrictionScore.js';
import { StepNode, type StepNodeData } from './StepNode.js';
import './journey-lineage.css';

const FRICTION_LEVELS: readonly FrictionLevel[] = ['low', 'watch', 'hard'];

const NODE_TYPES: NodeTypes = { step: StepNode as NodeTypes[string] };

const COLUMN_WIDTH = 312;
/** A short journey is fitted at its natural size, never blown up past it. */
const FIT_MAX_ZOOM = 1;
/** Tall enough for a wrap edge to travel between rows without crossing a node. */
const ROW_HEIGHT = 232;

interface JourneyLineageProps {
  graph: JourneyGraph;
  selectedStepId: string | null;
  onSelectStep: (stepId: string) => void;
}

/**
 * Interactive lineage of the user journey, reconstituted from measurement.
 * Nodes are steps, edges are observed transitions weighted by session volume.
 *
 * Steps are laid out in reading order — left to right, wrapping to the next
 * row — so the numbering always ascends the way the eye travels.
 */
export function JourneyLineage({ graph, selectedStepId, onSelectStep }: JourneyLineageProps) {
  const perRow = useMemo(() => stepsPerRow(graph.steps.length), [graph.steps.length]);

  const layout = useMemo<Node<StepNodeData>[]>(() => {
    const guidanceByStep = countGuidance(graph);
    return graph.steps.map((step, index) => ({
      id: step.id,
      type: 'step',
      position: {
        x: (index % perRow) * COLUMN_WIDTH,
        y: Math.floor(index / perRow) * ROW_HEIGHT,
      },
      data: {
        name: step.name,
        pagePath: step.pagePathPattern,
        position: step.position,
        sessionCount: step.sessionCount,
        dropOffRate: step.dropOffRate,
        frictionScore: step.frictionScore,
        guidanceCount: guidanceByStep.get(step.id)?.live ?? 0,
        proposedCount: guidanceByStep.get(step.id)?.proposed ?? 0,
        isStale: step.staleSince !== null,
      },
    }));
  }, [graph, perRow]);

  // Positions are local to the reader: they may rearrange the canvas freely,
  // and a new analysis run re-lays it out rather than freezing a hand layout.
  const [nodes, setNodes] = useState<Node<StepNodeData>[]>(layout);
  useEffect(() => setNodes(layout), [layout]);

  const onNodesChange = useCallback(
    (changes: NodeChange<Node<StepNodeData>>[]) => setNodes((current) => applyNodeChanges(changes, current)),
    [],
  );

  // Selection is applied at render time so it never disturbs dragged positions.
  const renderedNodes = useMemo(
    () =>
      nodes.map((node) =>
        node.selected === (node.id === selectedStepId)
          ? node
          : { ...node, selected: node.id === selectedStepId },
      ),
    [nodes, selectedStepId],
  );

  const edges = useMemo<Edge[]>(() => {
    const rowOf = new Map(graph.steps.map((step, index) => [step.id, Math.floor(index / perRow)]));
    const orderOf = new Map(graph.steps.map((step, index) => [step.id, index]));
    const maxVolume = Math.max(1, ...graph.transitions.map((t) => t.sessionCount));

    return graph.transitions.map((transition) => {
      const fromRow = rowOf.get(transition.fromStepId) ?? 0;
      const toRow = rowOf.get(transition.toStepId) ?? 0;
      const forward = (orderOf.get(transition.toStepId) ?? 0) > (orderOf.get(transition.fromStepId) ?? 0);

      // Inside a row the flow is horizontal; a change of row leaves from the
      // bottom and enters the top, so the edge never runs backwards.
      const sameRowForward = fromRow === toRow && forward;
      const weight = transition.sessionCount / maxVolume;

      return {
        id: `${transition.fromStepId}->${transition.toStepId}`,
        source: transition.fromStepId,
        target: transition.toStepId,
        sourceHandle: sameRowForward ? 'out-right' : 'out-bottom',
        targetHandle: sameRowForward ? 'in-left' : 'in-top',
        type: 'smoothstep',
        pathOptions: { borderRadius: 14 },
        animated: weight > 0.6,
        label: `${transition.sessionCount.toLocaleString('fr-FR')} usagers →`,
        labelBgPadding: [6, 3] as [number, number],
        labelBgBorderRadius: 6,
        labelBgStyle: { fill: '#ffffff', stroke: '#eaedf1' },
        labelStyle: { fontSize: 10.5, fontWeight: 600, fill: '#5b6472' },
        // Thickness carries the volume, so the main path reads immediately.
        style: { strokeWidth: 1 + weight * 3, stroke: '#c9d0da' },
      } satisfies BuiltInEdge;
    });
  }, [graph.steps, graph.transitions, perRow]);

  return (
    <div className="lineage">
      <ReactFlow
        nodes={renderedNodes}
        edges={edges}
        nodeTypes={NODE_TYPES}
        onNodesChange={onNodesChange}
        onNodeClick={(_, node) => onSelectStep(node.id)}
        fitView
        fitViewOptions={{ padding: 0.18, maxZoom: FIT_MAX_ZOOM }}
        minZoom={0.3}
        maxZoom={1.6}
        proOptions={{ hideAttribution: true }}
        nodesDraggable
        nodesConnectable={false}
        elevateNodesOnSelect
      >
        <Background variant={BackgroundVariant.Dots} gap={18} size={1} color="#dfe3e9" />
        <Controls showInteractive={false} position="bottom-right" />
      </ReactFlow>
      <ul className="lineage__legend" aria-label="Légende">
        {FRICTION_LEVELS.map((level) => (
          <li key={level}>
            <span className={`lineage__swatch lineage__swatch--${level}`} /> {FRICTION_LEVEL_LABELS[level]} (
            {FRICTION_LEVEL_RANGES[level]})
          </li>
        ))}
        <li className="lineage__legend-note">Une flèche = les usagers passés d'une page à la suivante</li>
      </ul>
    </div>
  );
}

/**
 * Rows are balanced rather than filled: 12 steps read as 4-4-4, not 5-5-2.
 * Capped at five so a long journey still fits the canvas at a legible zoom.
 * A short journey stays on a single line.
 */
const MAX_PER_ROW = 5;

function stepsPerRow(total: number): number {
  if (total <= MAX_PER_ROW) return total || 1;
  return Math.ceil(total / Math.ceil(total / MAX_PER_ROW));
}

function countGuidance(graph: JourneyGraph): Map<string, { live: number; proposed: number }> {
  const counts = new Map<string, { live: number; proposed: number }>();
  for (const guidance of graph.guidances) {
    const entry = counts.get(guidance.stepId) ?? { live: 0, proposed: 0 };
    if (guidance.state === 'published') entry.live += 1;
    if (guidance.state === 'proposed' || guidance.state === 'approved') entry.proposed += 1;
    counts.set(guidance.stepId, entry);
  }
  return counts;
}
